import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import tinycolor from 'tinycolor2';
import { Hue, Saturation } from 'react-color/lib/components/common';
import ChromePointer from 'react-color/lib/components/chrome/ChromePointer';
import ChromePointerCircle from 'react-color/lib/components/chrome/ChromePointerCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles, createStyles } from '../../styles';
import Column from '../../layout/Column';
import Row from '../../layout/Row';
import Spacer from '../../layout/Spacer';
import ActionBar from '../ActionBar/v2';
import InputLabel from '../InputLabel';
import TextField from '../TextField';
import NumberField from '../NumberField';
export const ColorPicker = ({ value, onChange, onClose }) => {
    const classes = useStyles();
    // Refs
    const originalColor = React.useRef(tinycolor(value));
    // State
    const [hex, setHex] = React.useState(originalColor.current.toHex());
    const [hsl, setHsl] = React.useState(originalColor.current.toHsl());
    const [hsv, setHsv] = React.useState(originalColor.current.toHsv());
    const [opacity, setOpacity] = React.useState(originalColor.current.getAlpha() * 100);
    // Memoizers
    const isDirty = React.useMemo(() => {
        const updatedColor = tinycolor(hex);
        updatedColor.setAlpha(opacity / 100);
        return (updatedColor.toHex() !== originalColor.current.toHex() ||
            updatedColor.getAlpha() !== originalColor.current.getAlpha());
    }, [hex, opacity]);
    // Callbacks
    const handleColorChange = React.useCallback((data, opacity) => {
        const color = tinycolor(data);
        const updatedHsl = color.toHsl();
        const updatedHsv = color.toHsv();
        const updatedHex = color.toHex();
        setOpacity(opacity);
        if (typeof data === 'string') {
            // Hex text field was updated, always save current value in state.
            setHex(data);
            // Don't update the sliders if the color isn't valid.
            if (color.isValid()) {
                setHsl(updatedHsl);
                setHsv(updatedHsv);
            }
        }
        else {
            // The sliders were updated, reset the hue value from data to fix an issue
            // where the hue slider doesn't update when the color is #ffffff.
            updatedHsl.h = data.h;
            updatedHsv.h = data.h;
            setHsl(updatedHsl);
            setHsv(updatedHsv);
            setHex(updatedHex);
        }
        color.setAlpha(opacity / 100);
        if (color.isValid()) {
            if (color.getAlpha() < 1) {
                onChange(color.toRgbString());
            }
            else {
                onChange(color.toHexString());
            }
        }
    }, [onChange]);
    const handleCancel = React.useCallback(() => {
        setHex(originalColor.current.toHex());
        setHsl(originalColor.current.toHsl());
        setHsv(originalColor.current.toHsv());
        setOpacity(originalColor.current.getAlpha() * 100);
        if (originalColor.current.isValid()) {
            if (originalColor.current.getAlpha() < 1) {
                onChange(originalColor.current.toRgbString());
            }
            else {
                onChange(originalColor.current.toHexString());
            }
        }
        onClose();
    }, [onChange, onClose]);
    // Render
    return (_jsxs("div", Object.assign({ className: classes.root }, { children: [_jsxs(Column, { children: [_jsx("div", Object.assign({ className: classes.saturation }, { children: _jsx(Saturation, { hsl: hsl, hsv: hsv, pointer: ChromePointerCircle, onChange: (value) => handleColorChange(value, opacity) }) })), _jsxs("div", { children: [_jsx(InputLabel, { children: "Hue" }), _jsx("div", Object.assign({ className: classes.slider }, { children: _jsx(Hue, { hsl: hsl, hsv: hsv, pointer: ChromePointer, onChange: (value) => handleColorChange(value, opacity) }) }))] }), _jsx(TextField, { label: "Hex", value: hex, onChange: (hex) => handleColorChange(hex, opacity) }), _jsx(NumberField, { label: "Opacity", value: opacity, min: 0, max: 100, onChange: (opacity) => handleColorChange(hex, opacity) })] }), _jsxs(Row, Object.assign({ className: classes.actions }, { children: [_jsx(ActionBar.Action, { icon: _jsx(CancelIcon, {}), color: "error", label: "Cancel", onClick: handleCancel }), _jsx(Spacer, {}), _jsx(ActionBar.Action, { icon: _jsx(CheckCircleIcon, {}), color: "success", label: "Done", disabled: !isDirty, onClick: onClose })] }))] })));
};
const useStyles = makeStyles((theme) => createStyles({
    root: {
        padding: theme.spacing(3),
        maxWidth: 240,
    },
    saturation: {
        position: 'relative',
        width: '100%',
        height: 100,
    },
    slider: {
        position: 'relative',
        width: '100%',
        height: 10,
    },
    actions: {
        marginTop: theme.spacing(3),
        paddingTop: theme.spacing(3),
        borderTop: `1px solid ${theme.actionBar.border}`,
    },
}));
export default ColorPicker;
//# sourceMappingURL=ColorPicker.js.map